import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Breadcrumb, BreadcrumbItem, BreadcrumbLink,
    BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog, DialogContent, DialogDescription,
    DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
    Search, Loader2, RefreshCw, ChevronLeft, ChevronRight,
    DollarSign, Clock, AlertCircle, RotateCcw, XCircle, Eye,
    CreditCard, MessageSquare,
} from 'lucide-react';
import { cardAdminApi, Recharge, RechargeStatus } from '../../api/cards.api';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';

const formatUSD = (v?: number) =>
    v != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v) : '—';

const formatHTG = (v?: number) =>
    v != null ? new Intl.NumberFormat('fr-HT', { style: 'currency', currency: 'HTG' }).format(v) : '—';

const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const STATUS_COLOR: Record<string, string> = {
    INITIATED: 'bg-slate-50 text-slate-500 border-slate-200',
    FUNDS_HELD: 'bg-blue-50 text-blue-600 border-blue-200',
    FX_PRICED: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    PROVIDER_RECHARGE_SUBMITTED: 'bg-violet-50 text-violet-600 border-violet-200',
    PROVIDER_CALLBACK_RECEIVED: 'bg-cyan-50 text-cyan-600 border-cyan-200',
    PROVIDER_CONFIRMED_SUCCESS: 'bg-green-50 text-green-700 border-green-200',
    PROVIDER_CONFIRMED_FAIL: 'bg-red-50 text-red-600 border-red-200',
    PROVIDER_REFUND_PENDING: 'bg-amber-50 text-amber-600 border-amber-200',
    PROVIDER_REFUNDED: 'bg-orange-50 text-orange-600 border-orange-200',
    LEDGER_FINALIZED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const STATUS_LABEL: Record<string, string> = {
    INITIATED: 'Kòmanse',
    FUNDS_HELD: 'Fon bloke',
    FX_PRICED: 'FX kalkile',
    PROVIDER_RECHARGE_SUBMITTED: 'Voye bay founisè',
    PROVIDER_CALLBACK_RECEIVED: 'Repon resevwa',
    PROVIDER_CONFIRMED_SUCCESS: 'Siksè ✓',
    PROVIDER_CONFIRMED_FAIL: 'Echwe ✗',
    PROVIDER_REFUND_PENDING: 'Ranbousman an atant',
    PROVIDER_REFUNDED: 'Ranbouse',
    LEDGER_FINALIZED: 'Finalize',
};

const STATUS_FILTERS: { key: RechargeStatus | 'all'; label: string }[] = [
    { key: 'all', label: 'Tout' },
    { key: 'PROVIDER_CONFIRMED_SUCCESS', label: 'Siksè' },
    { key: 'PROVIDER_CONFIRMED_FAIL', label: 'Echwe' },
    { key: 'FUNDS_HELD', label: 'An kou' },
    { key: 'PROVIDER_REFUNDED', label: 'Ranbouse' },
];

type ActionType = 'retry' | 'refund';

export default function RechargesPage() {
    const { toast } = useToast();

    const [recharges, setRecharges] = useState<Recharge[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<RechargeStatus | 'all'>('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const LIMIT = 20;

    const [selected, setSelected] = useState<Recharge | null>(null);
    const [actionType, setActionType] = useState<ActionType | null>(null);
    const [actionNote, setActionNote] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const loadRecharges = useCallback(async () => {
        try {
            setLoading(true);
            const res = await cardAdminApi.listRecharges({
                page, limit: LIMIT,
                status: statusFilter,
            });
            setRecharges(Array.isArray(res?.results) ? res.results : []);
            setTotal(res?.total ?? 0);
        } catch {
            setRecharges([]);
            toast({ title: 'Erè', description: 'Pa kapab chaje rechaj yo', type: 'error' });
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, statusFilter]);

    useEffect(() => { loadRecharges(); }, [loadRecharges]);
    useEffect(() => { setPage(1); }, [statusFilter, searchTerm]);

    const handleAction = async () => {
        if (!selected || !actionType) return;
        if (actionType === 'refund' && !actionNote.trim()) {
            toast({ title: 'Atansyon', description: 'Rezon ranbousman obligatwa', type: 'error' });
            return;
        }
        try {
            setActionLoading(true);
            if (actionType === 'retry') await cardAdminApi.retryRecharge(selected.id);
            else await cardAdminApi.refundRecharge(selected.id, actionNote);
            toast({ title: 'Siksè ✓', description: actionType === 'retry' ? 'Rechaj relanse' : 'Ranbousman kòmanse', type: 'success' });
            setSelected(null);
            setActionType(null);
            setActionNote('');
            loadRecharges();
        } catch (err: any) {
            toast({ title: 'Erè', description: err.response?.data?.error || 'Yon erè rive', type: 'error' });
        } finally {
            setActionLoading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / LIMIT));
    const filtered = recharges.filter(r =>
        !searchTerm ||
        r.id.includes(searchTerm) ||
        r.cardId.includes(searchTerm) ||
        r.holder?.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.providerRef && r.providerRef.includes(searchTerm))
    );

    return (
        <div className="flex h-screen overflow-hidden">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 space-y-6">

                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem><BreadcrumbLink>Kat VISA</BreadcrumbLink></BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem><BreadcrumbPage>Rechaj</BreadcrumbPage></BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Rechaj Kat</h1>
                            <p className="text-slate-500 text-sm mt-1">Suivi rechaj ak jere echèk ak ranbousman.</p>
                        </div>
                        <Button variant="outline" size="sm" className="gap-2 border-slate-200 self-start" onClick={loadRecharges}>
                            <RefreshCw className="h-4 w-4" /> Aktyalize
                        </Button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <Card className="border-none shadow-sm bg-gradient-to-br from-primary-600 to-primary-700 text-white">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-primary-100 text-xs font-semibold uppercase">Total</p>
                                <p className="text-3xl font-bold mt-1">{total}</p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-white border border-slate-100">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-slate-400 text-xs font-semibold uppercase">Siksè</p>
                                <p className="text-3xl font-bold mt-1 text-green-600">
                                    {recharges.filter(r => r.status === 'PROVIDER_CONFIRMED_SUCCESS' || r.status === 'LEDGER_FINALIZED').length}
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-white border border-slate-100">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-slate-400 text-xs font-semibold uppercase">Echwe</p>
                                <p className="text-3xl font-bold mt-1 text-red-600">
                                    {recharges.filter(r => r.status === 'PROVIDER_CONFIRMED_FAIL').length}
                                </p>
                            </CardContent>
                        </Card>
                        <Card className="border-none shadow-sm bg-white border border-slate-100">
                            <CardContent className="pt-5 pb-4">
                                <p className="text-slate-400 text-xs font-semibold uppercase">An kou</p>
                                <p className="text-3xl font-bold mt-1 text-amber-600">
                                    {recharges.filter(r => ['INITIATED', 'FUNDS_HELD', 'FX_PRICED', 'PROVIDER_RECHARGE_SUBMITTED'].includes(r.status)).length}
                                </p>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Tabs */}
                    <div className="flex items-center gap-1 bg-white p-1 rounded-xl border border-slate-200 overflow-x-auto">
                        {STATUS_FILTERS.map(({ key, label }) => (
                            <button key={key} onClick={() => setStatusFilter(key as RechargeStatus | 'all')}
                                className={cn('flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap',
                                    statusFilter === key ? 'bg-primary-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'
                                )}
                            >{label}</button>
                        ))}
                    </div>

                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                        <Input placeholder="Chèche pa ID, nimewo kat, oswa referans founisè..." className="pl-10 h-10 border-slate-200 rounded-xl" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>

                    {/* Table */}
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/50 border-b border-slate-200">
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Titilè / Kat</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Montan HTG</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Montan USD</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Estati</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Dat</th>
                                        <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Aksyon</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {loading ? (
                                        <tr><td colSpan={6} className="px-6 py-16 text-center">
                                            <Loader2 className="h-8 w-8 animate-spin text-primary-500 mx-auto" />
                                            <p className="text-slate-500 mt-3 text-sm">Ap chaje rechaj yo...</p>
                                        </td></tr>
                                    ) : filtered.length === 0 ? (
                                        <tr><td colSpan={6} className="px-6 py-16 text-center">
                                            <DollarSign className="h-10 w-10 text-slate-200 mx-auto" />
                                            <p className="text-slate-500 mt-3 font-medium">Okenn rechaj jwenn</p>
                                        </td></tr>
                                    ) : filtered.map((r) => (
                                        <tr key={r.id} className="hover:bg-slate-50/50 transition-colors">
                                            <td className="px-6 py-4">
                                                <p className="text-sm font-bold text-slate-900">{r.holder?.fullName ?? 'Titilè'}</p>
                                                <p className="text-xs text-slate-400 font-mono mt-0.5">•••• {r.holder?.lastFour ?? '????'}</p>
                                            </td>
                                            <td className="px-6 py-4 text-sm font-bold text-slate-900">{formatHTG(r.amountHtg)}</td>
                                            <td className="px-6 py-4 text-sm font-semibold text-slate-700">{formatUSD(r.amountUsdt)}</td>
                                            <td className="px-6 py-4">
                                                <Badge variant="outline" className={cn('text-xs font-semibold', STATUS_COLOR[r.status] ?? 'bg-slate-50 text-slate-500')}>
                                                    {STATUS_LABEL[r.status] ?? r.status}
                                                </Badge>
                                                {r.failureReason && (
                                                    <p className="text-xs text-red-500 mt-1 max-w-[200px] truncate">{r.failureReason}</p>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                                    <Clock className="h-3.5 w-3.5" />
                                                    {formatDate(r.initiatedAt)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelected(r)}>
                                                        <Eye className="h-4 w-4 text-slate-400" />
                                                    </Button>
                                                    {r.status === 'PROVIDER_CONFIRMED_FAIL' && (
                                                        <Button size="sm" variant="outline" className="text-blue-600 border-blue-100 hover:bg-blue-50 h-8 gap-1"
                                                            onClick={() => { setSelected(r); setActionType('retry'); }}>
                                                            <RotateCcw className="h-3.5 w-3.5" /> Relanse
                                                        </Button>
                                                    )}
                                                    {['FUNDS_HELD', 'PROVIDER_CONFIRMED_FAIL', 'FX_PRICED'].includes(r.status) && (
                                                        <Button size="sm" variant="outline" className="text-amber-600 border-amber-100 hover:bg-amber-50 h-8 gap-1"
                                                            onClick={() => { setSelected(r); setActionType('refund'); }}>
                                                            <XCircle className="h-3.5 w-3.5" /> Ranbouse
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                                <p className="text-sm text-slate-500">{(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} sou {total}</p>
                                <div className="flex items-center gap-2">
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                                    <span className="text-sm font-medium">{page}</span>
                                    <Button variant="outline" size="sm" className="h-8 w-8 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>

            {/* Detail Modal */}
            <Dialog open={!!selected && !actionType} onOpenChange={(o) => { if (!o) setSelected(null); }}>
                <DialogContent className="sm:max-w-md">
                    {selected && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <DollarSign className="h-5 w-5 text-primary-500" /> Rechaj — Detay
                                </DialogTitle>
                                <DialogDescription>
                                    <Badge variant="outline" className={cn('text-xs', STATUS_COLOR[selected.status])}>
                                        {STATUS_LABEL[selected.status] ?? selected.status}
                                    </Badge>
                                </DialogDescription>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-3 py-2 text-sm">
                                {[
                                    { l: 'ID', v: selected.id, mono: true },
                                    { l: 'Kat', v: `•••• ${selected.holder?.lastFour ?? '????'}` },
                                    { l: 'Montan HTG', v: formatHTG(selected.amountHtg) },
                                    { l: 'Montan USD', v: formatUSD(selected.amountUsdt) },
                                    { l: 'Taux FX', v: selected.fxRate ? `1 USD = ${selected.fxRate.toFixed(2)} HTG` : '—' },
                                    { l: 'Ref founisè', v: selected.providerRef, mono: true },
                                    { l: 'Kòmanse', v: formatDate(selected.initiatedAt) },
                                    { l: 'Konplete', v: formatDate(selected.completedAt) },
                                ].map(({ l, v, mono }) => (
                                    <div key={l}>
                                        <p className="text-xs text-slate-400">{l}</p>
                                        <p className={cn('font-semibold text-slate-800 mt-0.5', mono ? 'font-mono text-xs' : '')}>{v ?? '—'}</p>
                                    </div>
                                ))}
                                {selected.failureReason && (
                                    <div className="col-span-2 bg-red-50 rounded-lg p-3 border border-red-100">
                                        <p className="text-xs text-red-500 font-bold uppercase">Rezon echèk</p>
                                        <p className="text-sm text-red-700 mt-1">{selected.failureReason}</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Action Dialog */}
            <Dialog open={!!selected && !!actionType} onOpenChange={(o) => { if (!o) { setActionType(null); setActionNote(''); } }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {actionType === 'retry' ? <><RotateCcw className="h-5 w-5 text-blue-600" /> Relanse Rechaj</> : <><XCircle className="h-5 w-5 text-amber-500" /> Ranbousman</>}
                        </DialogTitle>
                        <DialogDescription>Rechaj <span className="font-mono font-bold">{selected?.id?.slice(0, 12)}…</span></DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        {actionType === 'refund' && (
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                    <MessageSquare className="h-4 w-4 text-slate-400" /> Rezon *
                                </label>
                                <Textarea placeholder="Rezon ranbousman obligatwa..." className="resize-none min-h-[80px]" value={actionNote} onChange={(e) => setActionNote(e.target.value)} />
                                {!actionNote.trim() && <p className="text-xs text-red-500 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Obligatwa</p>}
                            </div>
                        )}
                        {actionType === 'retry' && (
                            <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50 border border-blue-100">
                                <RotateCcw className="h-4 w-4 text-blue-600 mt-0.5" />
                                <p className="text-sm text-blue-800">Rechaj la ap voye bay HyperPay ankò.</p>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" className="border-slate-200" onClick={() => { setActionType(null); setActionNote(''); }}>Anile</Button>
                        <Button
                            className={cn('gap-2 shadow-md', actionType === 'retry' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-amber-500 hover:bg-amber-600')}
                            onClick={handleAction}
                            disabled={actionLoading || (actionType === 'refund' && !actionNote.trim())}
                        >
                            {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : actionType === 'retry' ? <RotateCcw className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                            {actionType === 'retry' ? 'Relanse' : 'Konfime Ranbousman'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
